/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  ExportOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import {
  canOpenSportsPayApplication,
  formatSportsPayState,
  resolveSportsPayAppLink,
} from '@sportninja/common/features/account/sportsPaySignUp'
import type { SportsPayOnboarding } from '@sportninja/common/features/account/sportsPaySignUp'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import { heading6, paragraphSmall, weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import { LABEL_LETTER_SPACING } from '../constants'

type SportsPayApplicationStatusProps = {
  application: SportsPayOnboarding
  /** Opens the embedded application. Only called when there is a usable link. */
  onOpen: (appLink: string) => void
  /** Set right after submitting, so the copy can acknowledge the new filing. */
  justCreated?: boolean
}

/** Final states that did not end in an approval. */
const UNSUCCESSFUL_STATES = new Set([
  'declined',
  'expired',
  'cancelled',
  'error',
])

/** One label/value pair from the application. */
const Detail: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <div css={detailStyle}>
    <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
      {capitalizeWords(label)}
    </Text>
    <Text variant='paragraphSmall' weight='semiBold' color={colors.NEUTRAL_0}>
      {value || '—'}
    </Text>
  </div>
)

/**
 * Shown instead of the form when the organization already has a SportsPay
 * application — either found by the opening fetch, or just filed.
 *
 * The Open button appears ONLY while the application is still in progress
 * (`canOpenSportsPayApplication`). Once it is submitted or final there is
 * nothing actionable on SportsPay's page, so the view drops to status only.
 *
 * `app_link` is validated before use: it is server-supplied and lands in an
 * iframe `src`, where a `javascript:` URL would execute.
 */
const SportsPayApplicationStatus: React.FC<SportsPayApplicationStatusProps> = ({
  application,
  onOpen,
  justCreated,
}) => {
  'use memo'

  const canOpen = canOpenSportsPayApplication(application)
  const appLink = resolveSportsPayAppLink(application.app_link)
  /*
   * A green check on a Declined or Expired application contradicts the Status
   * row right beneath it. The states that did not end well get a neutral
   * warning glyph instead — the Status value stays the source of truth either
   * way.
   */
  const isUnsuccessful = UNSUCCESSFUL_STATES.has(
    application.state?.trim().toLowerCase() ?? ''
  )

  return (
    <div css={wrapperStyle}>
      <div css={headerStyle}>
        {isUnsuccessful ? (
          <ExclamationCircleFilled css={warningStyle} />
        ) : (
          <CheckCircleFilled css={checkStyle} />
        )}
        <Text variant='heading6' weight='semiBold' color={colors.NEUTRAL_0}>
          {capitalizeWords(
            justCreated
              ? 'Your Application Has Been Started'
              : 'You Already Have An Application'
          )}
        </Text>
      </div>

      <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
        {canOpen
          ? 'Continue where you left off — the rest of the application needs to be completed on SportsPay.'
          : 'SportsPay has your application. There is nothing to do here for now — this status updates as they process it.'}
      </Text>

      <div css={detailsStyle}>
        <Detail
          label='Status'
          value={formatSportsPayState(application.state)}
        />
        <Detail label='Application ID' value={application.app_id} />
        {application.terminal_id ? (
          <Detail label='Terminal ID' value={application.terminal_id} />
        ) : null}
      </div>

      {canOpen && appLink ? (
        <button
          type='button'
          onClick={() => onOpen(appLink)}
          css={openButtonStyle}
        >
          <ExportOutlined css={openIconStyle} />
          {capitalizeWords('Open Application')}
        </button>
      ) : null}
    </div>
  )
}

export default SportsPayApplicationStatus

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * `align-items: stretch` (the default) rather than `flex-start`, so the details
 * box fills the panel instead of shrink-wrapping. The Open button opts back out
 * with `align-self: flex-start` — it should size to its label, not the row.
 */
const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;

  p {
    ${LABEL_LETTER_SPACING}
  }
`

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
`

const checkStyle = css`
  color: ${colors.PRIMARY};
  font-size: 20px;
`

/*
 * WARNING_300 rather than a tenant-aware token on purpose: this is a semantic
 * status colour, not branding, so it should read the same amber on SportNinja
 * and Canlan.
 */
const warningStyle = css`
  color: ${colors.WARNING_300};
  font-size: 20px;
`

const detailsStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_400};
`

const detailStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  /* Application and terminal ids are opaque strings with no spaces, so they
     need an explicit break to stay inside their column. */
  overflow-wrap: anywhere;
`

const openIconStyle = css`
  font-size: 14px;
  color: ${PRIMARY_BUTTON_CONTENT.icon};
`

const openButtonStyle = css`
  ${heading6}
  ${weightSemiBold}
  letter-spacing: 1px;
  display: flex;
  /* Sizes to its label rather than stretching, now that the column stretches. */
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 42px;
  padding: 8px 24px;
  background: ${colors.PRIMARY};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
  @media (max-width: 768px) {
    ${paragraphSmall}
    ${weightSemiBold}
    align-self: stretch;
  }
`
